import { useState } from 'react'
import { Button } from '@/components/ui/button'
import type { SparePart } from '@/types'
import { ShoppingCart, Minus, Plus } from 'lucide-react'
import { Input } from '@/components/ui/input'

interface PartViewProps {
  part: SparePart
  onAddToCart: (part: SparePart, quantity: number) => void
}

export function PartView({ part, onAddToCart }: PartViewProps) {
  const [quantity, setQuantity] = useState(1)
  const price = part.price ?? 0
  const currency = part.currency ?? 'RD$'
  const image = part.images?.[0]

  const handleAdd = () => {
    onAddToCart(part, quantity)
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="flex-1">
        <div className="aspect-square max-h-[50vh] w-full rounded-xl overflow-hidden bg-muted">
          {image ? (
            <img
              src={image}
              alt={part.name}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              Sin imagen
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 space-y-4">
        {part.sku && (
          <p className="text-sm text-muted-foreground">SKU: {part.sku}</p>
        )}
        <h2 className="text-2xl font-semibold">{part.name}</h2>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {part.brand?.name && <span>{part.brand.name}</span>}
          {part.category?.name && (
            <>
              <span>·</span>
              <span>{part.category.name}</span>
            </>
          )}
        </div>
        {part.description && (
          <p className="text-muted-foreground">{part.description}</p>
        )}
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-[#E10F18]">
            {currency} {price.toLocaleString()}
          </span>
        </div>
        {part.stock !== undefined && (
          <p className="text-sm text-muted-foreground">
            {part.stock > 0 ? `En stock: ${part.stock}` : 'Consultar disponibilidad'}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center rounded-lg border">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="w-14 h-9 text-center border-0 rounded-none"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setQuantity((q) => q + 1)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={handleAdd} className="gap-2">
            <ShoppingCart className="h-4 w-4" />
            Añadir al carrito
          </Button>
        </div>
      </div>
    </div>
  )
}
